import { sample, sumBy } from "lodash-es";
import * as THREE from "three/webgpu";
import { GameEngine } from "~/engine/instance";
import type { Voxel } from "~/terrain/voxel-terrain";
import { mapNumber } from "~/utils/math";
import { tmpColor } from "~/utils/three";
import { ParticleEmitter } from "./particle-emitter";

export type DrillEffectOptions = {
  particleVelocity?: THREE.Vector3;
  maxParticles?: number;
  particleSize?: number;
  gravity?: number;
};

export class DrillEffect {
  private emitter?: ParticleEmitter;
  private readonly velocity: THREE.Vector3;
  private readonly maxParticles: number;
  private readonly particleSize: number;
  private readonly particleGravity: number;

  constructor(options?: DrillEffectOptions) {
    this.velocity = options?.particleVelocity ?? new THREE.Vector3(0, 80.0, 0);
    this.maxParticles = options?.maxParticles ?? 256;
    this.particleSize = options?.particleSize ?? 3.0;
    this.particleGravity = options?.gravity ?? 150.0;
  }

  public display(drillingVoxels: Voxel[] | null, throughput?: number): void {
    const isDrilling = drillingVoxels != null && drillingVoxels.length > 0;

    if (!isDrilling) {
      this.emitter?.stopEmitting();
      return;
    }

    const voxelSize = GameEngine.voxelTerrain.voxelSize;

    if (!this.emitter) {
      this.emitter = new ParticleEmitter({
        maxParticles: this.maxParticles,
        gravity: this.particleGravity,
        angleVariance: 0.5,
        offsetVariance: voxelSize * 0.5,
        lifetime: 1,
        shape: "square",
        size: this.particleSize,
      });
      GameEngine.renderer.registerEntity(this.emitter.object3d);
    }

    const randomVoxel = sample(drillingVoxels)!;
    const color = tmpColor.setHex(randomVoxel.terrainInfo.color);
    color.offsetHSL(0, 0, 0.25 * Math.random());

    const worldPos = GameEngine.voxelTerrain.voxelToWorld(randomVoxel.voxelPos);
    const emitterPos = new THREE.Vector3(
      worldPos.x + voxelSize * 0.5,
      worldPos.y + voxelSize * 0.5,
      100,
    );

    throughput ??= mapNumber(
      sumBy(drillingVoxels, (r) => r.terrainInfo.resistance ?? 0),
      0.0,
      100.0,
      0.0,
      1.0,
      true,
    );

    this.emitter.startEmitting(emitterPos, this.velocity, color, throughput);
  }

  public dispose(): void {
    this.emitter?.dispose();
    this.emitter = undefined;
  }
}
